import { IUser } from 'core/interface/user';
import { ActivationTestRequest } from 'core/interface/activationTestPayload';
import { normalizeIndustry } from 'utils/industry';

export const serializeForJson = (value: unknown): unknown => {
  if (value instanceof File) {
    return {
      name: value.name,
      type: value.type,
      size: value.size
    };
  }
  if (Array.isArray(value)) {
    return value.map(serializeForJson);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        key,
        serializeForJson(nested)
      ])
    );
  }
  return value;
};

export const buildCompanyActivationRequests = (
  companyData: Record<string, any> | undefined,
  me: IUser
): ActivationTestRequest[] => {
  const data = companyData || {};
  const requests: ActivationTestRequest[] = [];

  if (data.first_name || data.last_name) {
    requests.push({
      id: 'account-name',
      method: 'PATCH',
      url: `/account/${me.id}/`,
      description: 'Update account name',
      body: {
        ...(data.first_name ? { first_name: data.first_name } : {}),
        ...(data.last_name ? { last_name: data.last_name } : {})
      }
    });
  }

  if (data.email) {
    requests.push({
      id: 'account-email',
      method: 'PATCH',
      url: `/account/${me.id}/`,
      description: 'Update account email',
      body: { email: data.email }
    });
  }

  if (data.newPassword && data.repeatPassword) {
    requests.push({
      id: 'password-change',
      method: 'PUT',
      url: '/account/auth/password/change/',
      description: 'Change password',
      body: {
        old_password: data.oldPassword || '',
        new_password: data.newPassword
      }
    });
  }

  if (data.avatar) {
    requests.push({
      id: 'account-avatar',
      method: 'PATCH',
      url: `/account/${me.id}/`,
      description: 'Upload account avatar',
      body: { avatar: serializeForJson(data.avatar) }
    });
  }

  const companyRequestBody: Record<string, unknown> = {};
  for (const key in data) {
    if (key === 'avatar' || key === 'id' || key === 'cultureProfile' || key === 'logo') {
      continue;
    }
    if (key === 'location' && data[key]) {
      companyRequestBody.location = {
        id: data[key].id,
        display_name: data[key].display_name
      };
    } else if (key === 'industry' && data[key]) {
      const industry = normalizeIndustry(data[key]);
      if (industry) {
        companyRequestBody.industry = industry;
      }
    } else {
      companyRequestBody[key] = data[key];
    }
  }

  requests.push({
    id: 'company-update',
    method: 'PUT',
    url: `/companies/${me.company}/`,
    description: 'Update company profile',
    body: serializeForJson(companyRequestBody)
  });

  requests.push({
    id: 'account-activated',
    method: 'PATCH',
    url: `/account/${me.id}/`,
    description: 'Mark account as activated',
    body: { is_new: false }
  });

  if (data.logo) {
    requests.push({
      id: 'company-logo',
      method: 'PATCH',
      url: `/companies/${me.company}/`,
      description: 'Upload company logo',
      body: { logo: serializeForJson(data.logo) }
    });
  }

  if (data.cultureProfile) {
    requests.push({
      id: 'culture-profile',
      method: 'POST',
      url: '/match/profiles/?culture=true',
      description: 'Create culture profile',
      body: serializeForJson(data.cultureProfile)
    });
  }

  return requests;
};

export const buildRecruiterActivationRequests = (
  companyData: Record<string, any> | undefined,
  me: IUser
): ActivationTestRequest[] => {
  const data = companyData || {};
  const requests: ActivationTestRequest[] = [];

  if (data.newPassword && data.repeatPassword) {
    requests.push({
      id: 'password-change',
      method: 'PUT',
      url: '/account/auth/password/change/',
      description: 'Change password',
      body: {
        old_password: data.oldPassword || '',
        new_password: data.newPassword
      }
    });
  }

  requests.push({
    id: 'recruiter-account',
    method: 'PATCH',
    url: `/account/${me.id}/`,
    description: 'Update recruiter account and mark as activated',
    body: serializeForJson({
      ...(data.first_name ? { first_name: data.first_name } : {}),
      ...(data.last_name ? { last_name: data.last_name } : {}),
      ...(data.job_title ? { job_title: data.job_title } : {}),
      ...(data.email ? { email: data.email } : {}),
      ...(data.avatar ? { avatar: data.avatar } : {}),
      is_new: false
    })
  });

  return requests;
};
